import { SAFE_COORDINATE_OUTSIDE_MAP } from "@/config/constants";
import {
	FrameName,
	tilerepoGrasslandFrameNames,
	tilerepoLayer1FrameNames,
	tilerepoLayer3FrameNames,
} from "@/game/entities/config";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions.ts";
import { QuestRewardTypes } from "@/game/entities/config/quests/quest_reward_types.ts";
import { quests } from "@/game/entities/config/quests/quests.ts";
import useEmitter, { EmitterEvents, TileLabelAction } from "@/hooks/useEmitter";
import useSegmentManager from "@/hooks/useSegmentManager";
import useTileRepository from "@/hooks/useTileRepository";
import router from "@/router";
import { getSettlementsStore } from "@/store/settlementsStoreRegistry";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore.ts";
import type {
	DiscoverSpecificTileObjective,
	FullSettlementsObjective,
	HaveSettlementAxisObjective,
	HaveSettlementsOnLevelObjective,
	HaveSpecificSettlementOnLevelObjective,
	QuestActive,
	QuestObjective,
} from "@/types/quest";
import { TileEntityParam, TileLabelSkin } from "@/types/tile";
import { RouteNames } from "@/types/ui";
import { getAmount } from "@/utils/number";
import type { QuestInProgressStates } from "./entities/config/quests/quest_states";
import { QuestTypes } from "./entities/config/quests/quest_types";
import {
	getQuestIdsByCoordinates,
	sideQuestsMarkers,
} from "./entities/config/quests/side_quests";
import { ResourceType } from "./entities/config/resources";
import { settlementNames } from "./entities/config/settlementNames";
import { isTerrain, TileVisibilityType } from "./entities/config/terrain";
import type TileEntity from "./entities/TileEntity";
import { plagueRepository } from "./PlagueRepository";

export function hasType(
	obj: unknown,
): obj is { type: FrameName | Set<FrameName> } {
	return typeof obj === "object" && obj !== null && "type" in obj;
}

export function hasSettlementId(
	obj: unknown,
): obj is { settlement_id: number } {
	return typeof obj === "object" && obj !== null && "settlement_id" in obj;
}

export function hasLevel(obj: unknown): obj is { level: number } {
	return typeof obj === "object" && obj !== null && "level" in obj;
}

export class QuestRepository {
	private static instance: QuestRepository;

	private constructor() {}

	public static getInstance(): QuestRepository {
		if (!this.instance) this.instance = new this();
		return this.instance;
	}

	public manageQuestState(
		action: QuestObjectiveActions | null,
		frameName: FrameName | null = null,
		progressIncrement: number = 1,
	): (QuestInProgressStates | void)[] {
		const questsInProgressStates: (QuestInProgressStates | void)[] = [];
		if (
			tilerepoLayer1FrameNames.has(frameName as FrameName) ||
			tilerepoLayer3FrameNames.has(frameName as FrameName)
		)
			return questsInProgressStates; // Skip terrain change quests for layer 1 and 3 frames

		const questStore = useQuestStore();
		const activeQuests = questStore.getActiveQuests;
		const tileRepo = useTileRepository();
		for (const quest of activeQuests) {
			const questConfig = quests.get(quest.id);
			if (!questConfig) continue;
			const { objective } = questConfig;

			if (!this.shouldHandleObjective(objective, action, frameName))
				continue;

			switch (objective.action) {
				case QuestObjectiveActions.CHANGE_TERRAIN:
					questStore.updateTerrainChangeCount(
						quest.id,
						frameName as FrameName,
					);
					continue;

				case QuestObjectiveActions.HAVE_SPECIFIC_SETTLEMENT_ON_LEVEL:
					questsInProgressStates.push(
						this.handleSpecificSettlementLevel(
							quest.id,
							objective as HaveSpecificSettlementOnLevelObjective,
						),
					);
					continue;

				case QuestObjectiveActions.SALVAGE:
					questsInProgressStates.push(
						this.handleSalvageObjective(
							quest.id,
							frameName as FrameName,
						),
					);
					continue;

				case QuestObjectiveActions.HAVE_SETTLEMENTS_ON_LEVEL:
					questsInProgressStates.push(
						this.handleSettlementsOnLevel(
							quest.id,
							objective as HaveSettlementsOnLevelObjective,
						),
					);
					continue;

				case QuestObjectiveActions.HAVE_FULL_SETTLEMENTS:
					questsInProgressStates.push(
						this.handleFullSettlements(
							quest.id,
							objective as FullSettlementsObjective,
						),
					);
					continue;

				case QuestObjectiveActions.HAVE_SETTLEMENTS_AXIS:
					questsInProgressStates.push(
						this.hadleSettlementOnAxis(
							quest.id,
							objective as HaveSettlementAxisObjective,
						),
					);
					continue;

				case QuestObjectiveActions.DISCOVER_SPECIFIC_TILE:
					questsInProgressStates.push(
						this.handleDiscoverSpecificTile(
							quest.id,
							objective as DiscoverSpecificTileObjective,
						),
					);
					continue;

				case QuestObjectiveActions.USE_RESOURCES:
					questStore.setPayedQuestState(quest.id);
					break;

				case QuestObjectiveActions.BUILD_BUILDING:
					questStore.updateBuiltBuildingCount(quest.id);
					continue;

				case QuestObjectiveActions.INCREASE_RESOURCE_INCOME:
					questStore.updateResourceIncomeProgress(quest.id);
					continue;

				case QuestObjectiveActions.CLEAR_PLAGUE:
					questsInProgressStates.push(
						this.handlePlagueObjective(quest.id),
					);
					continue;
				default:
					break;
			}

			if (objective.action === QuestObjectiveActions.HAVE_TERRAIN) {
				if (
					frameName &&
					[
						...objective.type,
						FrameName.NOT_SET,
						...tilerepoGrasslandFrameNames,
					].includes(frameName)
				) {
					let count = 0;
					let isTrrn = false;
					[...objective.type].forEach((frameName: string): void => {
						if (isTerrain(frameName as FrameName)) {
							isTrrn = true;
						}
					});
					if (isTrrn) {
						count = tileRepo.getTileCount0(
							objective.type as Set<FrameName>,
							getAmount(objective.amount),
						);
					} else {
						count = tileRepo.getTileCount2(
							objective.type as Set<FrameName>,
							getAmount(objective.amount),
						);
					}
					questsInProgressStates.push(
						questStore.setQuestProgress(quest.id, count),
					);
					continue;
				} else {
					continue;
				}
			}

			// Default progress update
			questsInProgressStates.push(
				questStore.updateQuestProgress(quest.id, progressIncrement),
			);
			continue;
		}

		return questsInProgressStates;
	}

	private shouldHandleObjective(
		objective: QuestObjective,
		action: QuestObjectiveActions | null,
		frameName: FrameName | null,
	): boolean {
		// Actions that don't require frameName matching
		if (
			[
				QuestObjectiveActions.BUILD_BUILDING,
				QuestObjectiveActions.SALVAGE,
				QuestObjectiveActions.HAVE_FULL_SETTLEMENTS,
				QuestObjectiveActions.CLEAR_TILES,
				QuestObjectiveActions.USE_RESOURCES,
				QuestObjectiveActions.HAVE_TERRAIN,
				QuestObjectiveActions.USE_SUPER_CLICKER,
				QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				QuestObjectiveActions.HAVE_SPECIFIC_SETTLEMENT_ON_LEVEL,
				QuestObjectiveActions.HAVE_SETTLEMENTS_ON_LEVEL,
				QuestObjectiveActions.HAVE_SETTLEMENTS_AXIS,
				QuestObjectiveActions.COLLECT_COINS,
				QuestObjectiveActions.COMPLETE_DAILY_QUESTS,
				QuestObjectiveActions.CLICK_ON_SETTLEMENT,
				QuestObjectiveActions.WATCH_ADS,
				QuestObjectiveActions.SPEND_RESOURCES,
				QuestObjectiveActions.INCREASE_RESOURCE_INCOME,
				QuestObjectiveActions.PIGGY_BANK_PROGRESS,
				QuestObjectiveActions.CLEAR_PLAGUE,
			].includes(objective.action)
		) {
			return objective.action === action;
		}

		// CHANGE_TERRAIN requires frameName matching
		if (objective.action === QuestObjectiveActions.CHANGE_TERRAIN) {
			return (
				action === QuestObjectiveActions.CHANGE_TERRAIN &&
				hasType(objective) &&
				objective.type === frameName
			);
		}

		// DISCOVER_SPECIFIC_TILE always returns true
		if (objective.action === QuestObjectiveActions.DISCOVER_SPECIFIC_TILE) {
			return true;
		}

		return false;
	}

	private handleSpecificSettlementLevel(
		questId: number,
		objective: HaveSpecificSettlementOnLevelObjective,
	): QuestInProgressStates | void {
		const actualSettlement = settlementNames.get(objective.settlement_id);
		if (!actualSettlement) return;
		const settlementStore = getSettlementsStore();
		const settlementLevel = settlementStore.getLevelByCoordinates({
			x: actualSettlement.x,
			y: actualSettlement.y,
		});
		if (settlementLevel) {
			return useQuestStore().setQuestProgress(
				questId,
				settlementLevel.value,
			);
		}
		return;
	}

	private handleSalvageObjective(
		questId: number,
		frameName: FrameName,
	): QuestInProgressStates | void {
		const questStore = useQuestStore();
		const quest = questStore.getActiveQuests.find(
			(q: QuestActive): boolean => q.id === questId,
		);
		if (!quest) return;

		if (hasType(quest.objective) && quest.objective.type === frameName) {
			return questStore.updateQuestProgress(questId, 1);
		}
	}

	private handlePlagueObjective(
		questId: number,
	): QuestInProgressStates | void {
		const questStore = useQuestStore();
		const quest = questStore.getActiveQuests.find(
			(q: QuestActive): boolean => q.id === questId,
		);

		if (!quest) return;

		return questStore.updateQuestProgress(questId, 1);
	}

	private handleSettlementsOnLevel(
		questId: number,
		objective: HaveSettlementsOnLevelObjective,
	): QuestInProgressStates {
		const tileRepo = useTileRepository();
		const questStore = useQuestStore();
		const settlementAmount =
			tileRepo.getActiveSettlementCountOnLevelOrHigher(objective.level) ??
			0;
		return questStore.setQuestProgress(questId, settlementAmount);
	}

	private hadleSettlementOnAxis(
		questId: number,
		objective: HaveSettlementAxisObjective,
	): QuestInProgressStates {
		const tileRepo = useTileRepository();
		const questStore = useQuestStore();
		const settlementAmount =
			tileRepo.getActiveSettlementCountOnLevelOrHigherAndAxis(
				objective.level,
				objective.axis,
			) ?? 0;
		return questStore.setQuestProgress(questId, settlementAmount);
	}

	private handleFullSettlements(
		questId: number,
		objective: FullSettlementsObjective,
	): QuestInProgressStates {
		// Go through all settlementEntities and call their haveFullSettlements getter count it and save it as the progress
		const tileRepo = useTileRepository();
		const questStore = useQuestStore();

		let settlementAmount = tileRepo.getFullSettlementsCount() ?? 0;

		if (getAmount(objective.amount) < settlementAmount) {
			settlementAmount = getAmount(objective.amount);
		}

		return questStore.setQuestProgress(questId, settlementAmount);
	}

	private handleDiscoverSpecificTile(
		questId: number,
		objective: DiscoverSpecificTileObjective,
	): QuestInProgressStates | void {
		if (objective.action !== QuestObjectiveActions.DISCOVER_SPECIFIC_TILE)
			return;
		const tileRepo = useTileRepository();
		const questStore = useQuestStore();
		const tile = tileRepo.getTileEntityByTileCoordinates({
			x: objective.coordination.x,
			y: objective.coordination.y,
		});

		if (
			tile &&
			tile.getParamByName(TileEntityParam.VISIBILITY) ===
				TileVisibilityType.EXPLORED
		) {
			useEmitter().emit(EmitterEvents.UnpingSpecificTile, {
				x: objective.coordination.x,
				y: objective.coordination.y,
			});
			return questStore.updateQuestProgress(questId, 1);
		}
		return;
	}

	public addReward(questId: number): void {
		const questConfig = quests.get(questId);
		if (!questConfig) return;

		const { reward } = questConfig;
		const resourcesStore = useResourcesStore();

		if (reward.type === QuestRewardTypes.RESOURCE && reward.content) {
			for (const r of reward.content) {
				resourcesStore.increase(
					r.reward_resource,
					getAmount(r.amount),
					"quest: reward",
				);
			}
		}
		if (
			reward.type === QuestRewardTypes.EXPLORATION_SPEED &&
			reward.content
		) {
			for (const r of reward.content) {
				const emitter: ReturnType<typeof useEmitter> = useEmitter();
				const resourcesStore = useResourcesStore();
				const { increase } = resourcesStore;
				increase(
					ResourceType.EXPLORATION_SPEED,
					getAmount(r.amount),
					"quest: reward",
				);
				//TODO toto je user dependent hodnota, default je 100 a navysuje sa upgradami atd, emit by mal chodit pri
				//TODO jej zmene ako naprikald update alebo ine akcie nie pri kazdom odhaleni tile
				emitter.emit(EmitterEvents.PhaserRecalculateExplorationSpeed);
			}
		}
		if (
			reward.type === QuestRewardTypes.RESOURCE_INCOME &&
			reward.content
		) {
			for (const r of reward.content) {
				useQuestStore().addRewardByTick(
					r.reward_resource,
					getAmount(r.amount),
				);
			}

			useSegmentManager().run(true);
		}

		if (
			reward.type === QuestRewardTypes.GET_BUILDING_TO_STOCK &&
			reward.content
		) {
			for (const r of reward.content) {
				useBuildingsStorageStore().increase(
					r.reward_resource,
					getAmount(r.amount),
					"quest: reward",
				);
			}
		}
	}

	public initializeSideQuestsMarkers(): void {
		// vybrat side questy, ktore nie su vo finished a active questoch
		// pozriet tie, ktore maju splnene prerequisites a emitnut event na zobrazenie markerov
		const questStore = useQuestStore();
		const { getFinishedQuestsIds, getActiveQuests: activeQuests } =
			questStore;
		useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
			coordinates: {
				x: SAFE_COORDINATE_OUTSIDE_MAP,
				y: SAFE_COORDINATE_OUTSIDE_MAP,
			},
			skin: TileLabelSkin.QUEST_SIDE_E,
			action: TileLabelAction.DELETE_ALL,
		});

		for (const [questId, marker] of sideQuestsMarkers) {
			if (
				!getFinishedQuestsIds().includes(questId) &&
				!activeQuests.some(
					(q: QuestActive): boolean => q.id === questId,
				) &&
				this.checkPrerequisites(questId)
			) {
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: marker.markerCoordinates,
					skin: TileLabelSkin.QUEST_SIDE_E,
					action: TileLabelAction.CREATE,
				});
			}
		}
	}

	private checkPrerequisites(questId: number): boolean {
		const marker = sideQuestsMarkers.get(questId);
		if (!marker) return false;

		const { prerequisites, markerCoordinates } = marker;
		const tilerepo = useTileRepository();
		const tile = tilerepo.getTileEntityByTileCoordinates(markerCoordinates);
		if (
			tile &&
			tile.getParamByName(TileEntityParam.VISIBILITY) !==
				TileVisibilityType.EXPLORED
		) {
			return false;
		}

		const questStore = useQuestStore();

		if (
			prerequisites.action ===
			QuestObjectiveActions.HAVE_SPECIFIC_SETTLEMENT_ON_LEVEL
		) {
			const actualSettlement = settlementNames.get(
				prerequisites.settlement_id,
			);
			if (!actualSettlement) return false;
			const settlementStore = getSettlementsStore();
			const settlementLevel = settlementStore.getLevelByCoordinates({
				x: actualSettlement.x,
				y: actualSettlement.y,
			});
			if (settlementLevel) {
				return settlementLevel.value >= getAmount(prerequisites.amount);
			}
			return false;
		}
		if (prerequisites.action === QuestObjectiveActions.FINISH_QUEST) {
			return questStore
				.getFinishedQuestsIds()
				.includes(prerequisites.quest_id);
		}
		return false;
	}

	public checkQuestsAfterTileReveal(clickedTile: TileEntity): void {
		getQuestIdsByCoordinates({
			x: clickedTile.x,
			y: clickedTile.y,
		}).forEach((questId: number): void => {
			if (this.checkPrerequisites(questId)) {
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: { x: clickedTile.x, y: clickedTile.y },
					skin: TileLabelSkin.QUEST_SIDE_E,
					action: TileLabelAction.CREATE,
				});
			}
		});
	}

	public showPopupSideQuestByCoordinates(coordinates: {
		x: number;
		y: number;
	}): void {
		const questIds = getQuestIdsByCoordinates(coordinates);
		const questStore = useQuestStore();
		// if questId is not in active quests and not in finished quests and prerequisites are met, show popup
		questIds.forEach((questId: number): void => {
			if (
				!questStore.getFinishedQuestsIds().includes(questId) &&
				!questStore.getActiveQuests.some(
					(q: QuestActive): boolean => q.id === questId,
				) &&
				this.checkPrerequisites(questId)
			) {
				router.push({
					name: RouteNames.QUESTS,
					params: {
						// don't forget that if we don't have at least a default value for params, it won't be properly reloaded after refresh
						tab: 0, // main
						// NOTE: if the in-progress active quest is displayed, show modal-window
						// NOTE: if it is pending, claim or generally tutorial, go through popup
						showPopup: "true",
						questPopupId: questId,
					},
				});
			}
		});
	}

	public activateSideQuest(questId: number): void {
		const questStore = useQuestStore();
		if (questId !== null) {
			const quest = quests.get(+questId);
			const marker = sideQuestsMarkers.get(questId);
			if (quest && marker) {
				questStore.addQuest(+questId, 0);
				if (
					quest.objective.action ===
					QuestObjectiveActions.CLEAR_PLAGUE
				) {
					plagueRepository.createPlague(
						quest.objective.range,
						quest.objective.coordination,
					);
				}
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: marker.markerCoordinates,
					skin: TileLabelSkin.QUEST_SIDE_E,
					action: TileLabelAction.DELETE,
				});
			} else {
				console.error("Chyba: Quest s daným ID neexistuje.");
			}
		}
	}

	public finishSideQuest(questId: number): void {
		// if prerequisites of next side quest contain this quest, show marker on map and show popup
		const questStore = useQuestStore();
		questStore.finishQuest(questId);
		const activeMainQuestId = questStore.getActiveQuests.find(
			(q: QuestActive): boolean => q.type === QuestTypes.MAIN,
		)?.id;
		if (questStore.trackedQuestId !== null && activeMainQuestId) {
			questStore.setTrackedQuest(activeMainQuestId);
		}
		sideQuestsMarkers.forEach((marker, id) => {
			if (
				id !== questId &&
				this.checkPrerequisites(id) &&
				marker.prerequisites.action ===
					QuestObjectiveActions.FINISH_QUEST &&
				marker.prerequisites.quest_id === questId
			) {
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: marker.markerCoordinates,
					skin: TileLabelSkin.QUEST_SIDE_E,
					action: TileLabelAction.CREATE,
				});
				this.showPopupSideQuestByCoordinates(marker.markerCoordinates);
			}
			if (id === questId) {
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: marker.markerCoordinates,
					skin: TileLabelSkin.QUEST_SIDE_E,
					action: TileLabelAction.DELETE,
				});
			}
		});
	}
}

export default QuestRepository;
